import { useCallback, useEffect, useRef, useState } from "react";
import { Headset, Loader2, LogOut, Send, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";

const HEARTBEAT_MS = 20000;

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");

  const errToast = (err) => {
    const d = err.response?.data?.detail;
    toast.error(typeof d === "string" ? d : "Nie udało się zalogować.");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/operator/login", { email, password });
      if (data.status === "otp_required") { setChallenge(data.challenge_id); setCode(""); }
      else onLogin(data);
    } catch (err) { errToast(err); } finally { setSending(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/operator/login/otp", { challenge_id: challenge, code: code.trim() });
      onLogin(data);
    } catch (err) { errToast(err); } finally { setSending(false); }
  };

  if (challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <form data-testid="operator-2fa-form" onSubmit={submitOtp} className="w-full max-w-sm bg-card border border-border rounded-2xl p-8 space-y-5">
          <div className="flex justify-center"><Logo /></div>
          <div className="text-center">
            <h1 className="font-display font-bold text-xl">Weryfikacja dwuetapowa</h1>
            <p className="text-xs text-muted-foreground mt-1">Wpisz 6-cyfrowy kod z aplikacji uwierzytelniającej.</p>
          </div>
          <Input data-testid="operator-2fa-input" autoFocus inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required className="bg-background text-center tracking-widest" />
          <button data-testid="operator-2fa-submit" type="submit" disabled={sending || code.trim().length !== 6} className="w-full bg-brand-teal text-white hover:bg-brand-tealDark disabled:opacity-60 font-semibold py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2">
            {sending && <Loader2 size={14} className="animate-spin" />} Zaloguj się
          </button>
          <button type="button" onClick={() => { setChallenge(""); setCode(""); }} className="w-full text-center text-xs text-muted-foreground hover:text-foreground">Wróć</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <form data-testid="operator-login-form" onSubmit={submit} className="w-full max-w-sm bg-card border border-border rounded-2xl p-8 space-y-5">
        <div className="flex justify-center"><Logo /></div>
        <div className="text-center">
          <h1 className="font-display font-bold text-xl">Konsola operatora czatu</h1>
          <p className="text-xs text-muted-foreground mt-1">Zaloguj się kontem administratora lub pracownika.</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">E-mail</label>
          <Input data-testid="operator-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-background" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Hasło</label>
          <Input data-testid="operator-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-background" />
        </div>
        <button data-testid="operator-login-button" type="submit" disabled={sending} className="w-full bg-brand-teal text-white hover:bg-brand-tealDark disabled:opacity-60 font-semibold py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2">
          {sending && <Loader2 size={14} className="animate-spin" />} Zaloguj się
        </button>
      </form>
    </div>
  );
}

function Console({ me, onLogout }) {
  const [busy, setBusy] = useState(!!me.busy);
  const [sessions, setSessions] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const prevUnreadRef = useRef(null);
  const audioRef = useRef(null);

  const beep = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioRef.current) audioRef.current = new Ctx();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.connect(g);
      g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.36);
    } catch { /* ignore */ }
  }, []);

  const loadSessions = useCallback(() => api.get("/operator/sessions").then((r) => {
    setSessions(r.data);
    const total = r.data.reduce((n, s) => n + (s.unread_admin || 0), 0);
    if (prevUnreadRef.current !== null && total > prevUnreadRef.current) {
      beep();
      document.title = "● Nowa wiadomość — Konsola operatora";
      setTimeout(() => { document.title = "Konsola operatora"; }, 4000);
    }
    prevUnreadRef.current = total;
  }).catch(() => setSessions([])), [beep]);
  const loadMessages = useCallback((sid) =>
    api.get(`/operator/sessions/${sid}/messages`).then((r) => {
      setMessages(r.data);
      requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }));
    }).catch(() => {}), []);

  // Heartbeat keeps the operator "online" so the widget shows the chat as active.
  useEffect(() => {
    const beat = () => api.post("/operator/heartbeat", { busy: busyRef.current }).catch(() => {});
    beat();
    const id = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    loadSessions();
    const id = setInterval(loadSessions, 5000);
    return () => clearInterval(id);
  }, [loadSessions]);

  useEffect(() => {
    if (!active) return undefined;
    loadMessages(active);
    const id = setInterval(() => loadMessages(active), 3000);
    return () => clearInterval(id);
  }, [active, loadMessages]);

  const toggleBusy = async () => {
    const next = !busy;
    setBusy(next);
    try {
      await api.post("/operator/heartbeat", { busy: next });
      toast.success(next ? "Ustawiono status: Zajęty — czat pokazuje „zostaw telefon”." : "Ustawiono status: Dostępny — czat aktywny.");
    } catch { /* ignore */ }
  };

  const reply = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    await api.post(`/operator/sessions/${active}/messages`, { text });
    setText("");
    loadMessages(active);
    loadSessions();
  };

  const remove = async (sid) => {
    await api.delete(`/operator/sessions/${sid}`);
    if (active === sid) setActive(null);
    loadSessions();
  };

  const logout = async () => {
    try { await api.post("/operator/logout"); } catch { /* ignore */ }
    onLogout();
  };

  const current = sessions?.find((s) => s.id === active);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Logo />
            <span className="text-sm font-semibold hidden sm:inline">Konsola operatora</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden md:inline" data-testid="operator-identity">{me.name} · {me.email}</span>
            <button
              data-testid="operator-busy-toggle"
              onClick={toggleBusy}
              className={`inline-flex items-center gap-2 text-xs font-semibold rounded-full px-4 py-2 border transition-colors ${busy ? "border-brand-yellow bg-brand-yellow/15 text-amber-700 dark:text-amber-300" : "border-brand-teal bg-brand-teal/10 text-brand-teal"}`}
            >
              <span className={`w-2 h-2 rounded-full ${busy ? "bg-brand-yellow" : "bg-brand-teal animate-pulse-dot"}`} />
              {busy ? "Zajęty" : "Dostępny"}
            </button>
            <button data-testid="operator-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
              <LogOut size={13} /> Wyloguj
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="mb-4 text-xs text-muted-foreground">
          Dopóki ta konsola jest otwarta i status to „Dostępny”, czat na stronie jest aktywny. Gdy zamkniesz konsolę lub ustawisz „Zajęty”, klienci zobaczą formularz „zostaw telefon”.
        </div>
        <div data-testid="operator-console" className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 min-h-[560px]">
          <div className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-border text-xs font-mono uppercase tracking-wider text-muted-foreground">
              Rozmowy ({sessions?.length ?? 0})
            </div>
            <div data-testid="operator-sessions-list" className="flex-1 overflow-y-auto divide-y divide-border">
              {!sessions ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>
              ) : sessions.length === 0 ? (
                <div data-testid="operator-sessions-empty" className="p-6 text-sm text-muted-foreground text-center">Brak rozmów.</div>
              ) : (
                sessions.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`operator-session-${s.id}`}
                    onClick={() => setActive(s.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-muted/60 transition-colors ${active === s.id ? "bg-muted" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold inline-flex items-center gap-1.5">
                        <Headset size={12} />
                        {s.lang?.toUpperCase()} · {new Date(s.updated_at).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {s.unread_admin > 0 && <span data-testid={`operator-session-unread-${s.id}`} className="text-[10px] font-mono bg-brand-red text-white px-1.5 py-0.5 rounded-full">{s.unread_admin}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">{s.last_message || "—"}</div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
            {!current ? (
              <div data-testid="operator-empty" className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-8 text-center">
                Wybierz rozmowę z listy, aby odpowiedzieć klientowi.
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold inline-flex items-center gap-1.5"><Headset size={13} /> Rozmowa · {current.lang?.toUpperCase()}</span>
                  <button data-testid={`operator-delete-${current.id}`} onClick={() => remove(current.id)} aria-label="Usuń rozmowę" className="p-2 rounded-full hover:bg-destructive/10 text-destructive">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div ref={listRef} data-testid="operator-messages" className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[480px]">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                      <div className="max-w-[80%]">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-1 mb-1 inline-flex items-center gap-1">
                          {m.role === "user" ? <UserRound size={10} /> : <Headset size={10} />}
                          {m.role === "user" ? "Klient" : m.role === "agent" ? "Konsultant" : "Twoje Biuro"} · {new Date(m.created_at).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                        <div className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-muted" : m.role === "agent" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "bg-slate-200/70 dark:bg-slate-800"}`}>
                          {m.text}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={reply} className="p-3 border-t border-border flex items-center gap-2">
                  <input
                    data-testid="operator-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Odpowiedz klientowi…"
                    className="flex-1 h-11 rounded-full border border-border bg-background px-4 text-sm outline-none focus:ring-2 focus:ring-slate-500/40"
                  />
                  <button data-testid="operator-send" type="submit" disabled={!text.trim()} aria-label="Wyślij" className="w-11 h-11 rounded-full bg-brand-teal text-white hover:bg-brand-tealDark flex items-center justify-center disabled:opacity-40">
                    <Send size={15} />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Operator() {
  const [me, setMe] = useState(undefined); // undefined = loading, null = logged out

  useEffect(() => {
    api.get("/operator/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  if (me === undefined)
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="animate-spin text-slate-600" /></div>;
  if (!me) return <LoginForm onLogin={setMe} />;
  return <Console me={me} onLogout={() => setMe(null)} />;
}
